import * as React from 'react';
import {useCallback} from 'react';
import {RunHistoryRow} from '../types/run';

import {
  mediaFilename,
  labelComponent,
  useStepWithRollback,
} from '../util/media';
import {runLink} from '../util/runhelpers';
import {Media} from '../util/runs';
import MessageMediaNotFound from './MessageMediaNotFound';

import memoize from 'memoize-one';
import {useLoadFile, useDownloadFile, FileInfo} from '../util/requests';
import {MediaCardProps} from './MediaCard';
import AudioViewer from './Panel2/AudioViewer';
import makeComp from '../util/profiler';

// This panel shows the audio for a single run, using wavesurfer.js to display the waveform

type AudioCardProps = MediaCardProps;

// Get the new-style Media object or null if there isn't one.

const getMedia = memoize(
  (
    stepData: RunHistoryRow | undefined,
    mediaKey: string,
    mediaIndex: number
  ): Media | null => {
    if (!stepData || !stepData[mediaKey]) {
      return null;
    }
    const stepMedia = stepData[mediaKey];
    if (stepMedia._type === 'audio') {
      if (stepMedia.audio) {
        return stepMedia.audio[mediaIndex];
      } else {
        return null;
      }
    } else if (stepMedia._type === 'audio-file') {
      return stepMedia;
    } else {
      return null;
    }
  }
);

export const AudioCard: React.FunctionComponent<AudioCardProps> = makeComp(
  props => {
    const {
      mediaKey,
      mediaIndex,
      run,
      runSignature,
      globalStep,
      height,
      scale,
    } = props;

    const downloadFile = useDownloadFile();

    const {currentStep, stepData, failedToLoad, rollback} = useStepWithRollback(
      props
    );

    const media =
      currentStep != null ? getMedia(stepData, mediaKey, mediaIndex) : null;

    let audioFile: string;
    if (media && media.path) {
      audioFile = media.path;
    } else {
      audioFile = mediaFilename(
        [mediaKey, currentStep, mediaIndex] as string[],
        'audio'
      );
    }

    const [fileUrl, setFileUrl] = React.useState<string>();
    const onSuccess = useCallback((fd: File, {directUrl}: FileInfo) => {
      setFileUrl(directUrl);
    }, []);

    // ! Warning: The callbacks passed to useLoadFile must stay
    // equal by reference or it will retrigger a new file download.
    useLoadFile(runSignature, audioFile, {
      responseType: 'file',
      onSuccess,
      fallback: rollback,
      skip: media == null || failedToLoad,
    });

    const mediaLoadFailed = media == null || failedToLoad;

    const titleLink = runLink(runSignature, run.displayName, {
      className: 'hide-in-run-page',
      target: '_blank',
      rel: 'noopener noreferrer',
    });

    // get caption
    let caption: string | undefined;
    if (media) {
      caption = media.caption;
    } else {
      caption =
        stepData &&
        stepData[mediaKey] &&
        stepData[mediaKey].captions &&
        stepData[mediaKey].captions[mediaIndex];
    }

    return (
      <AudioViewer
        audioSrc={fileUrl}
        caption={caption}
        height={height}
        scale={scale}
        mediaFailedToLoad={mediaLoadFailed}
        headerElement={labelComponent(props, currentStep, titleLink)}
        failedLoadElement={
          <MessageMediaNotFound
            mediaKey={mediaKey}
            stepIndex={globalStep}
            mediaIndex={mediaIndex}
            mediaType="audio"
          />
        }
        downloadFile={() => downloadFile(runSignature, audioFile)}
      />
    );
  },
  {id: 'AudioCard'}
);

export default AudioCard;
